import {Card, CardContent, CardHeader, IconButton} from "@mui/material";
import theme2 from "../../lib/theme";
import * as React from "react";
import {gql} from "@apollo/client";
import moment from "moment/moment";
import {query} from "../../lib/apollo";
import Loading from "../../components/loading";
import Box from "@mui/material/Box";
import CachedIcon from "@mui/icons-material/Cached";
import Timeseries from "../../components/timeseries";
import DateRange, {RangeToTimes} from "../../components/DateRange";

const DAY_SERIES = [
    {key: 'day2', name: '-2d', color: '#738ABA', zIndex: 6, legendIndex: 3},
    {key: 'day3', name: '-3d', color: '#CB6EF9', zIndex: 5, legendIndex: 4},
    {key: 'day4', name: '-4d', color: '#69DEC9', zIndex: 4, legendIndex: 5},
    {key: 'day5', name: '-5d', color: '#F9C469', zIndex: 3, legendIndex: 6},
    {key: 'day6', name: '-6d', color: '#ff9a00', zIndex: 2, legendIndex: 7},
    {key: 'day7', name: '-7d', color: '#a0609c', zIndex: 1, legendIndex: 8},
] as const;

function toSeriesData(items: {timestamp: number, value: number}[]) {
    return items.map((item) => [item.timestamp, item.value]);
}

export default function InventoryTimeseries(params: {selectedPublishers: any, reload: any}) {
    const [loading, setLoading] = React.useState(true);
    const [series, setSeries] = React.useState([] as any[]);
    const [averageDateRange, setAverageDateRange] = React.useState(RangeToTimes("Last 30 Days"));

    const reload = async () => {
        setLoading(true);

        const q = gql`
            query q($publisherIds: [Int!], $averageStartDate: String, $averageEndDate: String) {
                inventoryTimeSeries(publisherIds: $publisherIds, averageStartDate: $averageStartDate, averageEndDate: $averageEndDate) {
                    today {
                        timestamp
                        value
                    }
                    yesterday {
                        timestamp
                        value
                    }
                    day2 {
                        timestamp
                        value
                    }
                    day3 {
                        timestamp
                        value
                    }
                    day4 {
                        timestamp
                        value
                    }
                    day5 {
                        timestamp
                        value
                    }
                    day6 {
                        timestamp
                        value
                    }
                    day7 {
                        timestamp
                        value
                    }
                    average {
                        timestamp
                        value
                    }
                }
            }
        `;

        const variables = {
            publisherIds: params.selectedPublishers.map((p: any) => p.id),
            averageStartDate: moment(averageDateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
            averageEndDate: moment(averageDateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
        }

        const response = await query(q, variables);
        if (response && response.inventoryTimeSeries) {
            const s = response.inventoryTimeSeries;

            const series = [] as any[];

            // zIndex: Average on top, then Today, Yesterday, -2d ... -7d at bottom
            series.push({
                name: "Yesterday",
                data: toSeriesData(s.yesterday),
                type: 'area',
                color: '#7cb5ec',
                zIndex: 7,
                legendIndex: 0,
            });
            series.push({
                name: "Today",
                data: toSeriesData(s.today),
                type: 'area',
                color: '#90ed7d',
                zIndex: 8,
                legendIndex: 1,
            });
            series.push({
                name: "Average",
                data: toSeriesData(s.average),
                color: "red",
                zIndex: 9,
                legendIndex: 2,
            });

            DAY_SERIES.forEach(({key, name, color, zIndex, legendIndex}) => {
                series.push({
                    name,
                    data: toSeriesData(s[key]),
                    type: 'line',
                    color,
                    visible: false,
                    zIndex,
                    legendIndex,
                });
            });

            setSeries(series);
        }
        setLoading(false);
    }

    React.useEffect(() => {
        if (params.reload) {
            reload().then(() => {});
        }
    }, [params.reload, averageDateRange]);

    return (
        <Card sx={{height: '365px', maxWidth: 'calc(100vw - 10px)'}}>
            <CardHeader title={"Inventory By Hour"} sx={{backgroundColor: theme2.palette.primary.main, color: theme2.palette.primary.contrastText, padding: '5px'}}
                        titleTypographyProps={{sx:{fontSize: '18px'}}}
                        action={
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <DateRange showTime={false} dateRange={averageDateRange} setDateRange={setAverageDateRange} style={{width: '215px'}} placement={'bottomEnd'}/>
                                <IconButton aria-label="refresh" sx={{color: theme2.palette.primary.contrastText}} onClick={reload}>
                                    <CachedIcon />
                                </IconButton>
                            </Box>
                        }
            />
            <CardContent>
                {loading ? <Box sx={{marginTop: '130px'}}><Loading/></Box> : <>
                    <Timeseries height={300} series={series} title={"Leads Per Hour"} prefix={""} suffix= {""} dateFormat={"ha"} decimalPlaces={0}/>

                </>}
            </CardContent>
        </Card>
    );
}
